import type { ExpertRatingsPreviewRow } from './fixtures/marketingPreviewData';
import type { HeroLeaderboardPreviewRow } from './fixtures/marketingPreviewData';

function authorProfileRouteId(
  row: Pick<
    MarketingExpertRatingApiRow,
    'author_name' | 'ww_author_slug' | 'display_name'
  >,
): string {
  const slug = row.ww_author_slug?.trim();
  if (slug && /^\d{12}$/.test(slug)) return slug;
  return row.author_name?.trim() || '';
}

export type MarketingExpertRatingApiRow = {
  rank: number;
  author_name: string;
  ww_author_slug?: string | null;
  display_name?: string | null;
  author_image?: string | null;
  author_source?: string | null;
  credibility_score?: number | null;
  profit_rating?: number | null;
  roi_pct?: number | null;
  win_percentage?: number;
  total_picks?: number;
  correct_picks?: number;
  incorrect_picks?: number;
  tier?: string | null;
  rank_change?: number | null;
  rank_movement_new?: boolean | null;
};

export type MarketingExpertRatingsPayload = {
  data: MarketingExpertRatingApiRow[];
  updated_at: string;
  cache_ttl_seconds: number;
};

const DEFAULT_API_BASE = 'https://api.wagerwatchdog.com';

/**
 * Bump when Redis/payload semantics change. Cloudflare caches this GET by full
 * URL (see infra/cloudflare/terraform/marketing_api_cache.tf); a new query
 * bypasses a stuck edge object after Redis refresh.
 */
const MARKETING_EXPERT_RATINGS_URL_BUST = 'v2-250floor';

export function marketingExpertRatingsUrl(
  apiBaseUrl = DEFAULT_API_BASE,
): string {
  const path = `/api/v1/public/marketing/expert-ratings?v=${MARKETING_EXPERT_RATINGS_URL_BUST}`;
  const base = apiBaseUrl.replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

export async function fetchMarketingExpertRatings(
  apiBaseUrl = DEFAULT_API_BASE,
): Promise<MarketingExpertRatingsPayload> {
  const response = await fetch(marketingExpertRatingsUrl(apiBaseUrl), {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(
      `Marketing expert ratings request failed (${response.status})`,
    );
  }
  return response.json() as Promise<MarketingExpertRatingsPayload>;
}

export function expertProfileHref(
  appBaseUrl: string,
  row: Pick<
    MarketingExpertRatingApiRow,
    'author_name' | 'ww_author_slug' | 'display_name'
  >,
): string {
  const id = authorProfileRouteId(row);
  if (!id) return `${appBaseUrl.replace(/\/$/, '')}/ratings`;
  return `${appBaseUrl.replace(/\/$/, '')}/experts/profiles/${encodeURIComponent(id)}`;
}

export function toExpertRatingsPreviewRow(
  row: MarketingExpertRatingApiRow,
  appBaseUrl: string,
): ExpertRatingsPreviewRow & { profile_url: string } {
  return {
    rank: row.rank,
    rank_change: row.rank_change ?? null,
    rank_movement_new: row.rank_movement_new ?? null,
    author_name: row.author_name,
    author_source: row.author_source ?? null,
    credibility_score: Number(row.credibility_score ?? 0),
    profit_rating: Number(row.profit_rating ?? 0),
    roi_pct: Number(row.roi_pct ?? 0),
    correct_picks: Number(row.correct_picks ?? 0),
    incorrect_picks: Number(row.incorrect_picks ?? 0),
    total_picks: Number(row.total_picks ?? 0),
    win_percentage: Number(row.win_percentage ?? 0),
    profile_url: expertProfileHref(appBaseUrl, row),
  };
}

export function toHeroTeaserRow(
  row: MarketingExpertRatingApiRow,
  appBaseUrl: string,
): HeroLeaderboardPreviewRow & { profile_url: string } {
  const slug =
    row.ww_author_slug?.trim() ||
    row.author_name.toLowerCase().replace(/\s+/g, '-');
  return {
    key: slug,
    author_name: row.author_name,
    author_source: row.author_source ?? null,
    credibility_score: Number(row.credibility_score ?? 0),
    profit_rating: Number(row.profit_rating ?? 0),
    roi_pct: Number(row.roi_pct ?? 0),
    tier: row.tier ?? 'rising',
    win_percentage: Number(row.win_percentage ?? 0),
    total_picks: Number(row.total_picks ?? 0),
    correct_picks: Number(row.correct_picks ?? 0),
    profile_url: expertProfileHref(appBaseUrl, row),
  };
}
